'use client';

import classNames from 'classnames';
import Image from 'next/image';
import { Fragment, memo, useEffect, useRef, useState } from 'react';
import Skeleton from 'app/components/common/Skeleton';

function ImageBg({
  src,
  alt,
  fill,
  width,
  height,
  sizes,
  placeholderDataURL,
  skeletonTheme = 'sand',
  className,
  priority
}: {
  src: string;
  alt: string;
  fill?: boolean;
  width?: number;
  height?: number;
  sizes?: string;
  placeholderDataURL?: string;
  skeletonTheme?: 'sand' | 'gray' | 'brown' | 'graphite';
  className?: string;
  priority?: boolean;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(true);
  const ref = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (!ref.current?.complete) {
      setIsLoaded(false);
      setIsLoading(true);
    }
  }, []);

  return (
    <Fragment>
      {!placeholderDataURL && (
        <Skeleton
          theme={skeletonTheme}
          className={classNames(
            'absolute inset-0',
            'transition-opacity duration-200 ease-in',
            className,
            {
              'opacity-0': !isLoading
            }
          )}
        />
      )}
      <Image
        ref={ref}
        src={src}
        alt={alt}
        fill={fill}
        width={width}
        height={height}
        className={classNames(
          'transition-[opacity,filter] duration-200 object-cover ease-out',
          className,
          {
            'opacity-0 blur-3xl': !isLoaded,
            'blur-0': isLoaded
          }
        )}
        sizes={sizes}
        priority={priority}
        draggable={false}
        onLoad={() => {
          setIsLoaded(true);
          setIsLoading(false);
        }}
      />
      {!!placeholderDataURL && (
        <div
          className={classNames(
            'absolute inset-0',
            'transition-opacity duration-200 ease-in',
            'bg-no-repeat bg-center bg-cover blur-3xl',
            className,
            {
              'opacity-0': !isLoading
            }
          )}
          style={{ backgroundImage: `url(${placeholderDataURL})` }}
        />
      )}
    </Fragment>
  );
}

export default memo(ImageBg);
